import { Box, Button, Flex, Skeleton, Text } from '@chakra-ui/react';
import { useDisclaimerDrawer } from '@ui/components/DisclaimerDrawer';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { pathDict } from '@ui/config/paths';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import type { UseLcroDirectlyPartnerCardProps } from '@ui/hooks/useUseLcroPartnerCardsData';
import { useTranslations } from '@ui/i18n';
import { replaceRouterWithoutRerender } from '@ui/utils/urlHistory';
import NextImage from 'next/image';
import React from 'react';

import { useSupplyLcroModalStore } from './useSupplyLcroModalStore';

function UseLcroDirectlyPartnerCard({
  image,
  logo,
  description,
  buttonText,
  logoWidth,
  logoHeight,
  tvl,
  apy,
  type,
  comingSoon,
}: UseLcroDirectlyPartnerCardProps) {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[globalCoinMode];
  const openSupplyLcroModal = useSupplyLcroModalStore((s) => s.open);
  const openDisclaimerDrawer = useDisclaimerDrawer((s) => s.onOpen);

  const clickCta = () => {
    if (type === 'SINGLE') {
      openDisclaimerDrawer({
        url: 'https://app.singlefinance.io/farm',
        title: (
          <InDesktop>
            {t('Use your {coin}', {
              coin: 'LCRO',
            })}
          </InDesktop>
        ),
      });
      return;
    }
    if (type === 'SYNCSWAP') {
      openDisclaimerDrawer({
        url: 'https://syncswap.xyz/pool/0xDFdE5467560C82464a6A78926a88ca7E605a8dA0',
        title: (
          <InDesktop>
            {t('Use your {coin}', {
              coin: 'LETH',
            })}
          </InDesktop>
        ),
      });
      return;
    }
    if (type === 'Maverick') {
      openDisclaimerDrawer({
        url: 'https://app.mav.xyz/boosted-positions/0x42485ea94c9cb8fa37dcdb9342aedfd50e78191d?chain=324',
        title: (
          <InDesktop>
            {t('Use your {coin}', {
              coin: 'LETH',
            })}
          </InDesktop>
        ),
      });
      return;
    }
    if (type === 'VVS_TIA') {
      openDisclaimerDrawer({
        url: EXTERNAL_LINK.vvsTiaDeposit,
        title: (
          <InDesktop>
            {t('Use your {coin}', {
              coin: 'LTIA',
            })}
          </InDesktop>
        ),
      });
      return;
    }

    openSupplyLcroModal({ type, coinMode: globalCoinMode });
    replaceRouterWithoutRerender(
      pathDict[globalCoinMode].useLiquid + `/?deposit=${type.toLowerCase()}`,
    );
  };

  return (
    <Flex
      flexDir="column"
      py={4}
      px={2}
      gap={4}
      boxShadow="card.normal"
      bg="bg.main"
    >
      <Flex gap={4}>
        <Flex
          flexShrink="0"
          bg="bg.main"
          shadow="card.normal"
          rounded="full"
          w={{
            base: '70px',
            desktop: '87px',
          }}
          h={{
            base: '70px',
            desktop: '87px',
          }}
          p="4px"
          justify="stretch"
          align="stretch"
        >
          <Box position="relative" w="100%">
            <NextImage alt="Brand image" src={image} fill sizes="100vw" />
          </Box>
        </Flex>
        <Flex flexDir="column" justify="flex-start" align="flex-start">
          <NextImage
            width={logoWidth}
            height={logoHeight ?? '20'}
            alt="Brand image"
            src={logo}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
          {!comingSoon && (
            <>
              {tvl !== null && (
                <Flex gap={1} mt={2}>
                  <Skeleton minW="90px" isLoaded={!!tvl}>
                    <Text
                      as="span"
                      textStyle={{
                        base: 'bodySmall',
                        desktop: 'body',
                      }}
                      color="text.light"
                    >
                      {t('{coin} supplied', {
                        coin: coinModeInfo.liquidName,
                      })}{' '}
                    </Text>
                    <Text
                      as="span"
                      textStyle={{
                        base: 'bodySmallBold',
                        desktop: 'bodyBold',
                      }}
                    >
                      {tvl} USD
                    </Text>
                  </Skeleton>
                </Flex>
              )}
              {apy !== null && (
                <Skeleton minW="70px" isLoaded={apy !== undefined}>
                  <Text
                    mt={1}
                    textStyle={{
                      base: 'bodySmallBold',
                      desktop: 'h4Bold',
                    }}
                  >
                    {apy} APY
                  </Text>
                </Skeleton>
              )}
            </>
          )}
          {comingSoon && (
            <Flex mt="auto" mb={2} textStyle="bodyBold">
              {t('Coming soon!')}
            </Flex>
          )}
        </Flex>
        <Box flex="1" textAlign="right">
          <Button
            className={`veno-tour__use-liquid-${type.toLowerCase()}-supply`}
            mr={4}
            color="pink.500"
            variant="link"
            alignSelf="flex-start"
            disabled={comingSoon}
            onClick={clickCta}
          >
            {comingSoon ? t('Stay Tuned') : t('Supply')}
          </Button>
        </Box>
      </Flex>
      <Box
        textStyle={{
          base: 'bodySmall',
          desktop: 'body',
        }}
        color="text.light"
      >
        {description}
      </Box>
    </Flex>
  );
}

export default UseLcroDirectlyPartnerCard;
